import type { StoredJoinSplitNote } from "../joinsplit/types";

const VALUE_LIMIT = 1n << 120n;
const LEAF_LIMIT = 1 << 24;
const WORD = /^0x[0-9a-fA-F]{64}$/;

export interface DepositMergeScope {
  /** EVM chain key or the exact Solana network key. */
  network: string;
  pool: string;
  tokenHash: bigint;
  treeIndex: number;
}

/** An explicit release capability, not something inferred from wallet type. */
export interface DepositMergeCapability {
  family: "deposit-merge-v1";
  network: string;
  pool: string;
  enabled: boolean;
  feeMode: "none" | "private-fees";
  feeAdapterReady: boolean;
  recoveryReady: boolean;
}

export interface DepositMergeCandidate {
  /** Must come from authenticated inbox/tree reconciliation, not raw inbox data. */
  stored: StoredJoinSplitNote;
  scope: DepositMergeScope;
  confirmed: boolean;
  reserved: boolean;
}

export type DepositMergePlan =
  | {
      mode: "ordinary";
      reason: "unsupported" | "not-self" | "no-eligible-input";
    }
  | {
      mode: "merge";
      input: StoredJoinSplitNote;
      netCredit: bigint;
      /** Private client-only value. Never pass this object into a quote API. */
      outputValue: bigint;
    };

function sameScope(a: DepositMergeScope, b: DepositMergeScope): boolean {
  // Addresses are canonicalized when the chain adapter creates its scope.
  // Do not lowercase Solana base58 keys.
  return (
    a.network === b.network &&
    a.pool === b.pool &&
    a.tokenHash === b.tokenHash &&
    a.treeIndex === b.treeIndex
  );
}

function cloneStoredNote(stored: StoredJoinSplitNote): StoredJoinSplitNote {
  return {
    ...stored,
    note: {
      ...stored.note,
      tokenData: stored.note.tokenData
        ? {
            ...stored.note.tokenData,
            wrappedAsset: stored.note.tokenData.wrappedAsset
              ? { ...stored.note.tokenData.wrappedAsset }
              : undefined,
          }
        : undefined,
      privateHistory: stored.note.privateHistory
        ? { ...stored.note.privateHistory }
        : undefined,
    },
  };
}

function eligible(
  candidate: DepositMergeCandidate,
  scope: DepositMergeScope,
): boolean {
  const { note } = candidate.stored;
  return (
    candidate.confirmed &&
    !candidate.reserved &&
    !candidate.stored.spent &&
    sameScope(candidate.scope, scope) &&
    note.treeIndex === scope.treeIndex &&
    note.tokenHash === scope.tokenHash &&
    typeof note.value === "bigint" &&
    note.value > 0n &&
    note.value < VALUE_LIMIT &&
    Number.isSafeInteger(note.leafIndex) &&
    note.leafIndex >= 0 &&
    note.leafIndex < LEAF_LIMIT &&
    WORD.test(note.commitment)
  );
}

/**
 * Cheap, deterministic planning only. The preparation path must reauthenticate
 * owner, membership and unspent state under the wallet lock before proving.
 * There is deliberately no network I/O, signing, fee mutation or broad catch.
 */
export function planDepositMerge(params: {
  scope: DepositMergeScope;
  senderMpk: bigint;
  recipientMpk: bigint;
  netCredit: bigint;
  candidates: readonly DepositMergeCandidate[];
  capability?: DepositMergeCapability;
}): DepositMergePlan {
  const { scope, capability, netCredit } = params;
  if (
    typeof netCredit !== "bigint" ||
    netCredit <= 0n ||
    netCredit >= VALUE_LIMIT
  ) {
    throw new Error("Deposit credit must fit a positive uint120");
  }
  if (params.senderMpk !== params.recipientMpk) {
    return { mode: "ordinary", reason: "not-self" };
  }
  if (
    !capability?.enabled ||
    capability.family !== "deposit-merge-v1" ||
    capability.network !== scope.network ||
    capability.pool !== scope.pool ||
    !capability.recoveryReady ||
    (capability.feeMode === "private-fees" && !capability.feeAdapterReady)
  ) {
    return { mode: "ordinary", reason: "unsupported" };
  }
  let selected: StoredJoinSplitNote | undefined;
  for (const candidate of params.candidates) {
    if (
      !eligible(candidate, scope) ||
      candidate.stored.note.value + netCredit >= VALUE_LIMIT
    ) {
      continue;
    }
    const stored = candidate.stored;
    if (
      !selected ||
      stored.note.value > selected.note.value ||
      (stored.note.value === selected.note.value &&
        stored.note.leafIndex < selected.note.leafIndex)
    ) {
      selected = stored;
    }
  }
  if (!selected) {
    return { mode: "ordinary", reason: "no-eligible-input" };
  }
  return {
    mode: "merge",
    input: cloneStoredNote(selected),
    netCredit,
    outputValue: selected.note.value + netCredit,
  };
}

/** Exact two-input selection. It cannot accidentally choose the 32x5 ladder. */
export function planPairwiseConsolidation(params: {
  scope: DepositMergeScope;
  candidates: readonly DepositMergeCandidate[];
  sponsored: boolean;
  interactiveOperationPending: boolean;
  /** Includes every owner and fee output, not just the displayed balance. */
  outputCount: number;
}): readonly [StoredJoinSplitNote, StoredJoinSplitNote] | null {
  if (
    !params.sponsored ||
    params.interactiveOperationPending ||
    !Number.isInteger(params.outputCount) ||
    params.outputCount < 1 ||
    params.outputCount > 2
  ) {
    return null;
  }
  const seen = new Set<string>();
  // Retain only the two smallest values. If their sum overflows uint120 no
  // larger pair can fit, so no quadratic search is necessary.
  const smallest: StoredJoinSplitNote[] = [];
  for (const candidate of params.candidates) {
    if (!eligible(candidate, params.scope)) continue;
    const key = candidate.stored.note.commitment.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    smallest.push(candidate.stored);
    smallest.sort((a, b) =>
      a.note.value === b.note.value
        ? a.note.leafIndex - b.note.leafIndex
        : a.note.value < b.note.value
          ? -1
          : 1,
    );
    if (smallest.length > 2) smallest.pop();
  }
  if (
    smallest.length !== 2 ||
    smallest[0].note.value + smallest[1].note.value >= VALUE_LIMIT ||
    smallest[0].note.leafIndex === smallest[1].note.leafIndex
  ) {
    return null;
  }
  smallest.sort((a, b) => a.note.leafIndex - b.note.leafIndex);
  return [cloneStoredNote(smallest[0]), cloneStoredNote(smallest[1])];
}
